// Eyes, Doom and Devour (docs/04 §14). Once the machine can see (G_EYES), three or more visible
// Eyes NOTICE you (Doom +1, max 6). Each spin may be DEVOURED: always when a pattern holds three
// Teeth, otherwise with a Doom × 1.5% chance. A Halo blocks it; a Golden Tooth in the devouring
// pattern pays ×3; the Halo Fragment (once per window) and Saint's Knuckle turn it into a MIRACLE.
import { B } from '../content/balance';
import type { GridView } from '../content/defs';
import { say } from './dialogue';
import type { Modifiers } from './modifiers';
import { hashSeed, Rng } from './rng';
import type { GameEvent, GameState, PatternHit, RunState } from './types';

export type DevourKind = 'none' | 'blocked' | 'devour' | 'golden' | 'miracle';

export interface DevourResult {
  kind: DevourKind;
  cause?: 'teeth' | 'chance';
  /** How the MIRACLE came about. */
  via?: 'halofrag' | 'saint';
  /** Payout multiplier (1 when nothing happens). */
  x: number;
}

export function eyesActive(mods: Modifiers): boolean {
  return mods.features.has('eyes');
}

/** A per-spin roll that is the same for every dry-run, nudge and the real spin. */
function spinRoll(seed: string, spinIndex: number, salt: string): number {
  const r = new Rng(hashSeed(`${seed}|${spinIndex}|${salt}`));
  r.nextU32();
  return r.next();
}

export function devourCheck(run: RunState, mods: Modifiers, view: GridView, hits: PatternHit[], spinIndex: number): DevourResult {
  if (!eyesActive(mods)) return { kind: 'none', x: 1 };
  const cells = view.all();
  const toothPattern = hits.find((h) => h.cells.filter((c) => view.at(c.reel, c.row)?.def.tags.includes('tooth')).length >= mods.devourTeeth);
  const chance = run.doom * B.DEVOUR_PER_DOOM * mods.devourChanceX;
  const cause = toothPattern ? 'teeth' : spinRoll(run.seed, spinIndex, 'devour') < chance ? 'chance' : null;
  if (!cause) return { kind: 'none', x: 1 };
  if (cells.some((v) => v.def.blocksDevour)) return { kind: 'blocked', cause, x: 1 };
  if (mods.halofrag && !run.window.counters.halofrag) return { kind: 'miracle', cause, via: 'halofrag', x: B.MIRACLE_X };
  if (mods.miracleChance > 0 && spinRoll(run.seed, spinIndex, 'miracle') < mods.miracleChance) return { kind: 'miracle', cause, via: 'saint', x: B.MIRACLE_X };
  if (toothPattern && toothPattern.cells.some((c) => view.at(c.reel, c.row)?.def.goldenDevour)) return { kind: 'golden', cause, x: B.GOLDEN_DEVOUR_X };
  return { kind: 'devour', cause, x: mods.devourKeep };
}

/** Step 4: NOTICED — three or more Eyes raise Doom. Also remembers when an Eye was last seen. */
export function noticeEyes(state: GameState, mods: Modifiers, view: GridView, events: GameEvent[]): void {
  const run = state.run!;
  if (!eyesActive(mods)) return;
  const eyes = view.all().filter((v) => v.def.tags.includes('eye')).length;
  if (eyes > 0) run.flags.lastEyeSpin = run.spinIndex;
  if (eyes >= B.NOTICED_EYES && run.doom < mods.doomMax) {
    setDoom(state, mods, run.doom + 1, events, true);
    say(state, 'noticed', events, undefined, { force: true });
  }
}

export function setDoom(state: GameState, mods: Modifiers, value: number, events: GameEvent[], noticed = false): void {
  const run = state.run!;
  const next = Math.max(Math.min(mods.doomFloor, mods.doomMax), Math.min(mods.doomMax, value));
  if (next === run.doom) return;
  run.doom = next;
  events.push({ e: 'DOOM', value: next, noticed });
}

/** Window end: Doom decays when no Eye was seen in the last few spins. */
export function doomWindowEnd(state: GameState, mods: Modifiers, events: GameEvent[]): void {
  const run = state.run!;
  if (!eyesActive(mods) || run.doom <= 0) return;
  const quiet = run.spinIndex - (run.flags.lastEyeSpin ?? -Infinity) >= B.DOOM_QUIET_SPINS;
  if (quiet) setDoom(state, mods, run.doom - mods.doomDecayX, events);
}

/** Bank: what a Devour does beyond the payout (Hunger, the Halo Fragment's use, Jar of Teeth). */
export function applyDevour(state: GameState, mods: Modifiers, d: DevourResult, events: GameEvent[]): number {
  const run = state.run!;
  if (d.kind === 'none' || d.kind === 'blocked') {
    if (d.kind === 'blocked') events.push({ e: 'DEVOUR', kind: 'blocked' });
    return 0;
  }
  run.window.counters.devours = (run.window.counters.devours ?? 0) + 1;
  state.meta.lifetime.devours++;
  if (d.via === 'halofrag') run.window.counters.halofrag = 1;
  if (d.kind === 'miracle') state.meta.lifetime.miracles++;
  events.push({ e: 'DEVOUR', kind: d.kind, cause: d.cause, via: d.via });
  say(state, d.kind === 'miracle' ? 'miracle' : 'devour', events, undefined, { force: true });
  return d.kind === 'devour' || d.kind === 'golden' ? B.DEVOUR_HUNGER : 0;
}
